import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import '@/styles/tiptap-editor.css';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
}

export default function RichTextEditor({ 
  content, 
  onChange, 
  placeholder = '请输入内容...',
  minHeight = '200px'
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      Color,
    ],
    content,
    onUpdate: ({ editor }: { editor: Editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose-base max-w-none focus:outline-none tiptap-editor-content',
        style: `min-height: ${minHeight}`,
      },
    },
  });

  // Handle editor initialization properly for React 19
  if (!editor) {
    return (
      <div 
        className="border border-[#E5E7EB] rounded-lg overflow-hidden bg-white"
        style={{ minHeight: minHeight }}
      >
        <div className="flex items-center justify-center text-[#9CA3AF] text-sm">
          加载编辑器...
        </div>
      </div>
    );
  }

  const ToolbarButton = ({ 
    onClick, 
    active, 
    title, 
    children 
  }: { 
    onClick: () => void; 
    active?: boolean; 
    title: string; 
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-2 rounded-lg transition-colors ${
        active 
          ? 'bg-[#F59E0B] text-white' 
          : 'text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#18181B]'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-[#E5E7EB] rounded-lg overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-[#E5E7EB] bg-[#FAFAFA]">
        {/* Text Formatting */}
        <div className="flex items-center gap-0.5">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')}
            title="加粗"
          >
            <i className="ri-bold text-sm" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive('italic')}
            title="斜体"
          >
            <i className="ri-italic text-sm" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            active={editor.isActive('strike')}
            title="删除线"
          >
            <i className="ri-strikethrough text-sm" />
          </ToolbarButton>
        </div>

        <div className="w-px h-6 bg-[#E5E7EB] mx-1" />

        {/* Headings */}
        <div className="flex items-center gap-0.5">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            active={editor.isActive('heading', { level: 1 })}
            title="标题1"
          >
            <span className="text-xs font-bold">H1</span>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor.isActive('heading', { level: 2 })}
            title="标题2"
          >
            <span className="text-xs font-bold">H2</span>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            active={editor.isActive('heading', { level: 3 })}
            title="标题3"
          >
            <span className="text-xs font-bold">H3</span>
          </ToolbarButton>
        </div>

        <div className="w-px h-6 bg-[#E5E7EB] mx-1" />

        {/* Lists */}
        <div className="flex items-center gap-0.5">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive('bulletList')}
            title="无序列表"
          >
            <i className="ri-list-unordered text-sm" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive('orderedList')}
            title="有序列表"
          >
            <i className="ri-list-ordered text-sm" />
          </ToolbarButton>
        </div>

        <div className="w-px h-6 bg-[#E5E7EB] mx-1" />

        {/* Alignment */}
        <div className="flex items-center gap-0.5">
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            active={editor.isActive({ textAlign: 'left' })}
            title="左对齐"
          >
            <i className="ri-align-left text-sm" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            active={editor.isActive({ textAlign: 'center' })}
            title="居中对齐"
          >
            <i className="ri-align-center text-sm" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            active={editor.isActive({ textAlign: 'right' })}
            title="右对齐"
          >
            <i className="ri-align-right text-sm" />
          </ToolbarButton>
        </div>

        <div className="w-px h-6 bg-[#E5E7EB] mx-1" />

        {/* Blocks */}
        <div className="flex items-center gap-0.5">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            active={editor.isActive('blockquote')}
            title="引用"
          >
            <i className="ri-double-quotes-l text-sm" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            active={editor.isActive('codeBlock')}
            title="代码块"
          >
            <i className="ri-code-box-line text-sm" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            title="分隔线"
          >
            <i className="ri-separator text-sm" />
          </ToolbarButton>
        </div>

        <div className="w-px h-6 bg-[#E5E7EB] mx-1" />

        {/* Actions */}
        <div className="flex items-center gap-0.5">
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            active={false}
            title="撤销"
          >
            <i className="ri-arrow-go-back-line text-sm" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            active={false}
            title="重做"
          >
            <i className="ri-arrow-go-forward-line text-sm" />
          </ToolbarButton>
        </div>
      </div>

      {/* Editor Content */}
      <div className="px-4 py-3 overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
